import * as React from "react";
import { styled, Typography, SxProps, Stack, Box, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";

export type FormSectionProps = {
  className?: string;
  sx?: SxProps;
  children?: React.ReactNode;
  summaryContent?: React.ReactNode;
  title: string;
  status?: "complete" | "warning" | "error" | "todo" | "details" | "custom";
  statusText?: any;
  helperText?: string;
  icon?: React.ReactNode;
  size?: "mini" | "small" | "medium";
  withContainer?: boolean;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpand?: () => void;
  action?: React.ReactNode;
  customStatus?: React.ReactNode;
};

const FormSection: React.FC<FormSectionProps> = (props) => {
  const {
    children,
    icon = <></>,
    title,
    summaryContent,
    status,
    statusText,
    helperText,
    size = "medium",
    withContainer = true,
    defaultExpanded = false,
    expanded,
    onExpand,
    action,
    customStatus,
    ...otherProps
  } = props;

  const _helperText = helperText ? <Typography color="text.secondary">{helperText}</Typography> : <></>;

  return (
    <Box width="100%" {...otherProps}>
      <CardAccordion
        size={size}
        withContainer={withContainer}
        defaultExpanded={defaultExpanded}
        expanded={expanded}
        onChange={onExpand}
      >
        <AccordionSummary
          expandIcon={
            (defaultExpanded === true && expanded === true && !onExpand) ||
            (defaultExpanded === false && expanded === false && !onExpand) ? (
              <></>
            ) : (
              <>
                <AddIcon sx={{ color: "white" }} />
                <RemoveIcon sx={{ color: "white" }} />
              </>
            )
          }
          aria-controls="form-section-content"
          id="form-section-header"
        >
          <Stack>
            <StackTitle alignItems="center" direction="row" spacing={1} mb={statusText || helperText ? 1 : 0}>
              {icon}
              <Typography
                variant="h6"
                component="h3"
                fontWeight={size === "mini" ? 400 : 600}
                textTransform="uppercase"
                color="text.secondary"
              >
                {title}
              </Typography>
              {action}
            </StackTitle>
            {status === "complete" && (statusText || helperText) && (
              <Stack direction="row" alignItems="center" spacing={0.5} color="success.main">
                <Typography>{statusText}</Typography> <CheckIcon fontSize="small" />
              </Stack>
            )}
            {status === "error" && (statusText || helperText) && (
              <Stack direction="row" alignItems="center" spacing={0.5} color="error.main">
                <InfoOutlinedIcon fontSize="small" />
                <Typography>{statusText}</Typography>
                {_helperText}
              </Stack>
            )}
            {status === "todo" && (statusText || helperText) && (
              <Stack direction="row" alignItems="center" spacing={0.5} color="text.tier">
                <Typography color="primary.main">{statusText}</Typography>
                {_helperText}
              </Stack>
            )}
            {status === "warning" && (statusText || helperText) && (
              <Stack direction="row" alignItems="center" spacing={0.5} color="warning.main">
                <Typography>{statusText}</Typography>
                <BuildCircleOutlinedIcon fontSize="small" sx={{ ml: 0.5 }} />
                {_helperText}
              </Stack>
            )}
            {status === "details" && (statusText || helperText) && (
              <Stack direction="row" alignItems="center" spacing={0.5} color="text.tier">
                {statusText}
                {_helperText}
              </Stack>
            )}
            {status === "custom" && customStatus && customStatus}
          </Stack>
          {summaryContent && summaryContent}
        </AccordionSummary>
        <AccordionDetails>{children}</AccordionDetails>
      </CardAccordion>
    </Box>
  );
};

const CardAccordion = styled(Accordion, { shouldForwardProp: (prop) => prop !== "size" && prop !== "withContainer" })<{
  size: FormSectionProps["size"];
  withContainer: FormSectionProps["withContainer"];
}>(({ theme, size, withContainer }) => ({
  position: "relative",
  transition: "all .3s",
  paddingLeft: 0,
  paddingRight: 0,
  background: "transparent",
  border: `1px solid ${theme.palette.backgroundAlpha.border}`,

  "&:after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",

    borderRadius: 6,
  },
  ".MuiAccordionDetails-root": {
    display: "flex",
    flexDirection: "column",
    padding: withContainer ? theme.spacing(2) : 0,
    borderTop: `1px solid ${theme.palette.backgroundAlpha.border}`,
  },
  "&:hover": {
    background: theme.palette.backgroundAlpha.main,
  },
  "&.Mui-expanded": {
    background: theme.palette.mode === "dark" ? theme.palette.backgroundAlpha.main : "transparent",
    ".MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root": {
      "&:first-of-type": {
        display: "none",
      },
      "&:last-of-type": {
        display: "block",
      },
    },
    ".MuiAccordionSummary-root": {
      ...(size === "mini" && { minHeight: 32 }),
      ...(size === "small" && { minHeight: 48 }),
    },
  },
  ".MuiAccordionSummary-root": {
    paddingLeft: size === "mini" ? theme.spacing(1) : theme.spacing(2),
    paddingRight: theme.spacing(2),
    ...(size === "mini" && { minHeight: 32 }),
    ...(size === "small" && { minHeight: 48 }),
    "&:hover": {
      background: theme.palette.backgroundAlpha.hover,
    },
  },
  ".MuiAccordionSummary-content": {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: size === "mini" ? theme.spacing(0.75) : theme.spacing(1.5),
    marginBottom: size === "mini" ? theme.spacing(0.75) : theme.spacing(1.5),
    marginRight: size === "mini" ? theme.spacing(1) : theme.spacing(2),
  },

  ".MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root": {
    "&:first-of-type": {
      display: "block",
    },
    "&:last-of-type": {
      display: "none",
    },
  },
}));

const StackTitle = styled(Stack)(({ theme }) => ({
  ".MuiSvgIcon-root": {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
}));

export default FormSection;
